
  import { defineComponent, ref } from 'vue';
  import VChart from 'vue-echarts';
  import type { EChartsOption } from 'echarts';
  import { BarChart } from 'echarts/charts';
  import {
    DatasetComponent,
    GridComponent,
    LegendComponent,
    TitleComponent,
    TooltipComponent,
  } from 'echarts/components';
  import { use } from 'echarts/core';
  import { CanvasRenderer } from 'echarts/renderers';

  import { useAdaptiveGridBottom } from './composables/useAdaptiveGridBottom';
  import { useEChartsBarChart } from './composables/useEChartsBarChart';
  import { COLORS, ECHARTS_BAR_CHART_PROPS, ECHARTS_LEGEND_OPTIONS, ECHARTS_LOADING_OPTIONS } from './constants';
  import EChartsBase from './EChartsBase.vue';
  import EChartsScrollContainer from './EChartsScrollContainer.vue';
  import { TEChartsLoadingOptions } from './types';
  import { createTooltipPositioning, getEChartsGridOptionsByTitle, normalizeEChartsLabel } from './utils';

  use([GridComponent, LegendComponent, TitleComponent, TooltipComponent, BarChart, CanvasRenderer, DatasetComponent]);

  export default defineComponent({
    name: 'EChartsVerticalBar',
    components: {
      EChartsCore: VChart,
      EChartsBase,
      EChartsScrollContainer,
    },
    props: {
      ...ECHARTS_BAR_CHART_PROPS,
      yAxisName: {
        type: String,
        default: '',
      },
      yAxisMaxRatio: {
        type: Number,
        default: undefined,
      },
      legendInverseOrder: {
        type: Boolean,
        default: false,
      },
      minWidth: {
        type: Number,
      },
    },
    setup(props) {
      const chartRef = ref<InstanceType<typeof VChart> | null>(null);
      const { gridBottom } = useAdaptiveGridBottom({
        chartRef,
        getCategoryLabels: () => props.labels,
      });
      const barChart = useEChartsBarChart(props);
      const tooltipPositioning = createTooltipPositioning();

      return {
        chartRef,
        gridBottom,
        tooltipPositioning,
        ...barChart,
      };
    },
    computed: {
      getChartOption(): EChartsOption {
        const { max: yAxisMax, interval: yAxisInterval } = this.getValueAxisBounds(this.yAxisMaxRatio);

        return {
          tooltip: {
            show: this.showTooltip,
            trigger: 'axis',
            appendTo: this.tooltipPositioning.appendTo,
            position: this.tooltipPositioning.position,
            axisPointer: { type: 'line' },
            padding: 0,
            formatter: this.buildAxisTooltipFormatter,
          },
          dataset: {
            source: this.getDatasetSource,
          },
          color: this.getSeriesColors,
          legend: {
            ...ECHARTS_LEGEND_OPTIONS,
            icon: this.legendIcon,
            selectedMode: this.legendSelectable,
            ...(this.legendInverseOrder
              ? {
                  data: [...this.dataset]
                    .reverse()
                    .map((item) => normalizeEChartsLabel(item.label))
                    .filter(Boolean),
                }
              : {}),
          },
          grid: {
            ...getEChartsGridOptionsByTitle(this.titleConfig?.text),
            top: 40,
            bottom: this.gridBottom,
          },
          xAxis: {
            type: 'category',
            axisLine: {
              show: false,
            },
            axisTick: {
              show: false,
            },
            axisLabel: {
              interval: 0,
              hideOverlap: true,
              margin: 18,
              fontSize: 14,
              color: COLORS.lightGrey,
              fontFamily: 'Fira Sans',
              formatter: this.formatCategoryAxisLabel,
            },
          },
          yAxis: {
            name: this.yAxisName || undefined,
            nameLocation: 'end',
            nameGap: 20,
            nameTextStyle: {
              align: 'left',
              fontSize: 14,
              color: COLORS.lightGrey,
              fontFamily: 'Fira Sans',
            },
            axisLine: {
              show: false,
            },
            axisTick: {
              show: false,
            },
            axisLabel: {
              show: this.getHasData,
              fontSize: 14,
              color: COLORS.lightGrey,
              fontFamily: 'Fira Sans',
              formatter: this.formatAxisLabel,
            },
            splitLine: {
              show: true,
              lineStyle: {
                color: 'rgba(120, 120, 120, 0.1)',
              },
            },
            min: this.getHasData ? undefined : 0,
            max: this.getHasData ? yAxisMax : 1,
            interval: yAxisInterval,
          },
          series: this.buildBarSeries(this.stack ? 'inside' : 'top'),
        };
      },
      getLoadingOptions(): TEChartsLoadingOptions {
        return ECHARTS_LOADING_OPTIONS;
      },
    },
  });
